import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { getMapImageUrl } from '../../utils/formatters';
import { currentLocale } from '../../i18n';
import YouTubeEmbed from './YouTubeEmbed';
import LiveMatchHub from './LiveMatchHub';
import './LiveMatch.css';

const POLL_INTERVAL = 30000;

const GotvConnect = ({ address, password }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  if (!address) return null;

  const connectString = password
    ? `connect ${address}; password ${password}`
    : `connect ${address}`;

  const handleCopy = (e) => {
    e.stopPropagation();
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(connectString).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className="live-gotv" onClick={(e) => e.stopPropagation()}>
      <div className="live-gotv-label">GOTV</div>
      <div className="live-gotv-row">
        <code className="live-gotv-address">{address}</code>
        {password && (
          <>
            <span className="live-gotv-sep">·</span>
            <code className="live-gotv-password">{password}</code>
          </>
        )}
      </div>
      <button
        type="button"
        className="live-gotv-copy"
        onClick={handleCopy}
      >
        {copied ? t('live.copied') : t('live.copyConnect')}
      </button>
    </div>
  );
};

const LiveMatch = ({ liveMatches }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const streamInfo = useStreamInfo();
  const streamLive = !!(streamInfo?.is_streaming &&
    (streamInfo?.youtube_video_id || streamInfo?.youtube_channel_id));
  const streamEmbed = streamLive ? (
    <YouTubeEmbed
      channelId={streamInfo.youtube_channel_id}
      videoId={streamInfo.youtube_video_id}
    />
  ) : null;

  if (!liveMatches) {
    return (
      <div className="live-match-container">
        {streamEmbed}
        <div className="live-connecting">
          <div className="loader"></div>
          <span>{t('live.loading')}</span>
        </div>
      </div>
    );
  }

  // Ingen match är LIVE än — men om en stream är igång (t.ex. startad tidigt från
  // Redo-vyn) visar vi ändå embed:en + en live-indikering här.
  if (liveMatches.length === 0) {
    return (
      <div className="live-match-container">
        {streamEmbed}
        <div className="no-live-match">
          <span className="no-match-icon">{streamLive ? '🔴' : '🎮'}</span>
          <h2>{streamLive ? t('live.streamLiveTitle', 'Sändningen är live') : t('live.noMatchesTitle')}</h2>
          <p>{streamLive ? t('live.streamLiveDesc', 'Matchen visas här så snart den startar.') : t('live.noMatchesDesc')}</p>
        </div>
      </div>
    );
  }

  // Match-hubben (fas 3): första live-matchen får den fulla samlingsvyn
  // (score, win probability, tips, scoreboard, killfeed, kommentarer);
  // eventuella ytterligare samtidiga matcher listas som kort under.
  const [hubMatch, ...otherMatches] = liveMatches;

  return (
    <div className="live-match-container">
      {streamEmbed}
      <div className="live-hub-gotv-row">
        <GotvConnect
          address={hubMatch.gotv_address}
          password={hubMatch.gotv_password}
        />
      </div>
      <LiveMatchHub summary={hubMatch} />
      <div className="live-matches-list">
        {otherMatches.map((match) => {
          const mapImageUrl = match.map_image_url ? getMapImageUrl(match.map_image_url) : null;
          return (
            <div
              key={match.matchid}
              className="live-match-card"
              onClick={() => navigate(`/stats/matches/${match.matchid}`)}
              style={mapImageUrl ? { '--map-bg': `url(${mapImageUrl})` } : {}}
            >
              <div className="live-match-card-overlay">
                <div className="live-match-card-badge">
                  <span className="live-dot"></span>
                  LIVE
                </div>
                <div className="live-match-card-map">{match.mapname}</div>
                <div className="live-match-card-teams">
                  <span className="live-match-card-team">{match.team1_name}</span>
                  <span className="live-match-card-score">{match.team1_score} - {match.team2_score}</span>
                  <span className="live-match-card-team">{match.team2_name}</span>
                </div>
                {match.start_time && (
                  <div className="live-match-card-time">
                    {t('live.startedAt', { time: new Date(match.start_time).toLocaleTimeString(currentLocale(), { hour: '2-digit', minute: '2-digit' }) })}
                  </div>
                )}
                <GotvConnect
                  address={match.gotv_address}
                  password={match.gotv_password}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export function useLiveMatches() {
  const [liveMatches, setLiveMatches] = useState(null);

  useEffect(() => {
    const fetchLive = () => {
      fetch(`${API_URL}/api/matches/live`)
        .then(res => res.json())
        .then(data => setLiveMatches(data.matches || []))
        .catch(() => setLiveMatches([]));
    };

    fetchLive();
    const interval = setInterval(fetchLive, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  return liveMatches;
}

/**
 * Pollar stream-status (is_streaming + youtube_channel_id) från stats-backend.
 * Används för att visa YouTube-embed:en på Live-sidan när en match streamas.
 * Returnerar null tills första svaret / vid fel.
 */
export function useStreamInfo() {
  const [streamInfo, setStreamInfo] = useState(null);

  useEffect(() => {
    const fetchStream = () => {
      fetch(`${API_URL}/api/live/stream`)
        .then(res => res.json())
        .then(data => setStreamInfo(data))
        .catch(() => setStreamInfo(null));
    };

    fetchStream();
    const interval = setInterval(fetchStream, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  return streamInfo;
}

export default LiveMatch;
